import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatGridSkeleton } from "@/components/common";
import { PROJECT_STATUSES, PRIORITIES, item } from "@/lib/constants";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, FolderKanban, Loader2 } from "lucide-react";

export default function Projects() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", description: "", status: "planning", priority: "medium", client_id: "", budget: 0, color: "#4f46e5" });
  const [saving, setSaving] = useState(false);

  const { data = [], isLoading } = useQuery({ queryKey: ["projects"], queryFn: async () => { const d = (await api.get("/projects")).data; return Array.isArray(d) ? d : []; } });

  useEffect(() => { if (params.get("new")) { setOpen(true); setParams({}); } }, [params]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Proje adı gerekli"); return; }
    setSaving(true);
    try {
      await api.post("/projects", { ...form, budget: Number(form.budget) });
      toast.success("Proje oluşturuldu");
      qc.invalidateQueries({ queryKey: ["projects"] });
      setOpen(false);
      setForm({ name: "", code: "", description: "", status: "planning", priority: "medium", client_id: "", budget: 0, color: "#4f46e5" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Projeler" subtitle={`${data.length} proje`}
        actions={can("manage_projects") && <Button onClick={() => setOpen(true)} data-testid="new-project-button"><Plus className="h-4 w-4 mr-1.5" /> Yeni Proje</Button>} />

      {isLoading ? <StatGridSkeleton /> : data.length === 0 ? (
        <EmptyState icon={FolderKanban} title="Proje yok" description="İlk projenizi oluşturun." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((p) => {
            const st = item(PROJECT_STATUSES, p.status);
            return (
              <Card key={p.id} onClick={() => nav(`/projects/${p.id}`)} className="p-5 cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5" data-testid={`project-card-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-lg grid place-items-center text-white font-bold font-display" style={{ background: p.color }}>{p.name[0]}</div>
                  <Badge variant="outline" className={`text-[10px] ${st.chip}`}>{st.label}</Badge>
                </div>
                <h3 className="font-semibold mt-3 truncate">{p.name}</h3>
                <p className="text-xs text-muted-foreground font-mono">{p.code || "—"}</p>
                <div className="mt-3">
                  <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">İlerleme</span><span className="font-medium">{p.progress}%</span></div>
                  <Progress value={p.progress} className="h-1.5" />
                </div>
                <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                  <span>{p.completed_count}/{p.task_count} görev</span>
                  {p.budget > 0 && <span>{fmtCurrency(p.budget)}</span>}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="project-dialog">
          <DialogHeader><DialogTitle>Yeni Proje</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Proje Adı</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="project-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kod</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1.5" data-testid="project-code-input" /></div>
              <div><Label>Bütçe (₺)</Label><Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Açıklama</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{PROJECT_STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={create} disabled={saving} data-testid="project-save-button">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
